import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useGuestHashSystem from '@projects/systems/useGuestHashSystem';
import { QueryKeys } from '@projects/queries/queryKeys';
import { updateItems, updateQueryData } from 'src/new_arch/queries/updaters';
import { type GuestHashJSON } from '@projects/models/guestHash';

const useUpdateGuestHashMutation = ( { guestHashID, projectID }: { guestHashID: number, projectID: number } ) => {
	const system = useGuestHashSystem();
	const queryClient = useQueryClient();
	const queryKey = QueryKeys.forGuestHashes( projectID );

	const onMutate = useCallback( async ( enabled: boolean ) => {
		await queryClient.cancelQueries( { queryKey } );
		const prevGuestHashes = queryClient.getQueryData( queryKey );

		updateQueryData(
			queryClient,
			queryKey,
			updateItems<GuestHashJSON>(
				[ guestHashID ],
				item => ( { ...item, enabled } )
			)
		);

		return { prevGuestHashes };
	}, [ projectID, guestHashID ] );

	const onError = useCallback(
		( _error: unknown, _params: boolean, context?: { prevGuestHashes: unknown } ) => {
			queryClient.setQueryData( queryKey, context?.prevGuestHashes );
		},
		[ queryKey ]
	);

	return useMutation(
		( enabled: boolean ) => system.updateGuestHash( guestHashID, enabled ),
		{ onMutate, onError }
	);
}

export default useUpdateGuestHashMutation;
